import { Component, Input, Output, EventEmitter, signal, ElementRef, HostListener, computed, inject, effect, forwardRef } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
    selector: 'app-date-picker',
    standalone: true,
    imports: [CommonModule],
    providers: [
        DatePipe,
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DatePickerComponent),
            multi: true
        }
    ],
    template: `
    <div class="relative" (click)="$event.stopPropagation()">
      <!-- Trigger -->
      <div 
        (click)="toggle()"
        class="relative w-full h-11 bg-white border border-gray-300 rounded-lg shadow-sm cursor-pointer
               hover:border-enduro-orange/50 hover:shadow-md transition-all duration-200 
               flex items-center px-4 select-none"
        [class.border-enduro-orange]="isOpen()"
        [class.ring-4]="isOpen()"
        [class.ring-enduro-orange-10]="isOpen()">
        
        <!-- Icon -->
        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-400 mr-3 pointer-events-none group-hover:text-enduro-orange transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>

        <!-- Label -->
        <div class="flex-grow font-semibold text-sm tracking-wide" 
             [class.text-gray-700]="value" 
             [class.text-gray-400]="!value">
          {{ value ? (value | date:'dd/MM/yyyy') : placeholder }}
        </div>

        <!-- Chevron -->
        <svg xmlns="http://www.w3.org/2000/svg" 
             class="h-4 w-4 text-gray-300 transition-transform duration-200"
             [class.rotate-180]="isOpen()" 
             fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      <!-- Calendar Dropdown -->
      @if (isOpen()) {
        <div class="absolute top-full left-0 mt-2 bg-white rounded-lg shadow-xl border border-gray-100 p-4 z-50 animate-in fade-in zoom-in-95 duration-100 w-72">
          
          <!-- Header -->
          <div class="flex items-center justify-between mb-4">
            <button type="button" (click)="prevMonth()" class="p-1 hover:bg-gray-100 rounded-full text-gray-600 transition-colors" aria-label="Mese precedente">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path></svg>
            </button>
            <span class="font-bold text-gray-800 capitalize">
                {{ currentMonthName() }} {{ currentYear() }}
            </span>
            <button type="button" (click)="nextMonth()" class="p-1 hover:bg-gray-100 rounded-full text-gray-600 transition-colors" aria-label="Prossimo mese">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path></svg>
            </button>
          </div>

          <!-- Weekdays -->
          <div class="grid grid-cols-7 gap-1 mb-2 text-center text-xs font-bold text-gray-400 uppercase tracking-widest">
            @for (day of weekDays(); track day) {
                <div>{{ day }}</div>
            }
          </div>

          <!-- Days Grid -->
          <div class="grid grid-cols-7 gap-1">
            <!-- Empty slots -->
            @for (empty of emptySlots(); track $index) {
                <div></div>
            }
            <!-- Days -->
            @for (day of days(); track day) {
                <button 
                    type="button"
                    (click)="selectDate(day)"
                    class="h-8 w-8 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-200"
                    [class.bg-enduro-orange]="isSelected(day)"
                    [class.text-white]="isSelected(day)"
                    [class.hover:bg-orange-100]="!isSelected(day)"
                    [class.text-gray-700]="!isSelected(day)">
                    {{ day }}
                </button>
            }
          </div>
        </div>
      }
    </div>
  `
})
export class DatePickerComponent implements ControlValueAccessor {
    @Input() value: string = ''; // Format yyyy-mm-dd
    @Input() placeholder: string = 'Seleziona data...';
    @Output() valueChange = new EventEmitter<string>();

    translate = inject(TranslateService);
    isOpen = signal(false);

    // Track language change to trigger re-renders if necessary (mostly for computed dependency)
    currentLang = signal(this.translate.currentLang || 'it');

    // Calendar State
    viewDate = signal(new Date());

    onChange: any = () => { };
    onTouched: any = () => { };

    constructor(private elementRef: ElementRef) {
        this.translate.onLangChange.subscribe((event) => {
            this.currentLang.set(event.lang);
        });
    }

    writeValue(value: any): void {
        this.value = value || '';
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    currentMonthName = computed(() => {
        const lang = this.currentLang() === 'it' ? 'it-IT' : 'en-US';
        return this.viewDate().toLocaleString(lang, { month: 'long' });
    });

    weekDays = computed(() => {
        const lang = this.currentLang() === 'it' ? 'it-IT' : 'en-US';
        // Generate weekdays based on locale (Monday start)
        const days = [];
        for (let i = 1; i <= 7; i++) {
            // Jan 1 2001 was a Monday
            const d = new Date(2001, 0, i);
            days.push(d.toLocaleString(lang, { weekday: 'short' }).slice(0, 2));
        }
        return days;
    });

    currentYear = computed(() => {
        return this.viewDate().getFullYear();
    });

    days = computed(() => {
        const year = this.viewDate().getFullYear();
        const month = this.viewDate().getMonth();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        return Array.from({ length: daysInMonth }, (_, i) => i + 1);
    });

    emptySlots = computed(() => {
        const year = this.viewDate().getFullYear();
        const month = this.viewDate().getMonth();
        const firstDayIndex = new Date(year, month, 1).getDay(); // 0 = Sun, 1 = Mon ...
        // Adjust for Monday start (Mon=0, Sun=6)
        // Legacy: Sun(0) -> 6, Mon(1) -> 0
        return Array.from({ length: (firstDayIndex + 6) % 7 });
    });

    toggle() {
        this.isOpen.update(v => !v);
    }

    prevMonth() {
        this.viewDate.update(d => new Date(d.getFullYear(), d.getMonth() - 1, 1));
    }

    nextMonth() {
        this.viewDate.update(d => new Date(d.getFullYear(), d.getMonth() + 1, 1));
    }

    selectDate(day: number) {
        const year = this.viewDate().getFullYear();
        const month = this.viewDate().getMonth(); // 0-indexed
        // Format YYYY-MM-DD
        const date = new Date(Date.UTC(year, month, day));
        const dateString = date.toISOString().split('T')[0];

        this.value = dateString;
        this.valueChange.emit(this.value);
        this.onChange(this.value);
        this.onTouched();
        this.isOpen.set(false);
    }

    isSelected(day: number): boolean {
        if (!this.value) return false;
        const [y, m, d] = this.value.split('-').map(Number);
        const viewY = this.viewDate().getFullYear();
        const viewM = this.viewDate().getMonth() + 1; // 1-indexed for comparison
        return day === d && viewM === m && viewY === y;
    }

    @HostListener('document:click', ['$event'])
    onClickOutside(event: Event) {
        if (!this.elementRef.nativeElement.contains(event.target)) {
            this.isOpen.set(false);
            this.onTouched();
        }
    }
}
